import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowRight, Tag } from "lucide-react";
import FooterNew from "../components/FooterNew";
import { getBackendUrl } from "../utils/auth";

const estimateReadTime = (text = "") => {
  const wordsPerMinute = 220;
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.ceil(words / wordsPerMinute));
  return `${minutes} min read`;
};

const formatDate = (dateValue) => {
  if (!dateValue) return "Recent";

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "Recent";

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const normalizeImageUrl = (url = "") => {
  const value = String(url || "").trim();

  if (!value || value === "null" || value === "undefined") {
    return "";
  }

  if (value.startsWith("//")) {
    return `https:${value}`;
  }

  return value;
};

const resolveImageUrl = (item = {}) => {
  const candidates = [item?.image_url, item?.image, item?.urlToImage, item?.source_icon];

  for (const candidate of candidates) {
    const normalized = normalizeImageUrl(candidate);
    if (normalized) return normalized;
  }

  return "";
};

const normalizeTextForDedup = (value = "") => String(value || "")
  .toLowerCase()
  .replace(/\s+/g, " ")
  .trim();

const getArticleDedupKey = (article = {}) => {
  const title = normalizeTextForDedup(article?.title);
  const excerpt = normalizeTextForDedup(article?.excerpt).slice(0, 220);

  if (!title && !excerpt) {
    const link = normalizeTextForDedup(article?.link);
    return link ? `link:${link}` : `id:${article?.id || ""}`;
  }

  return `${title}|${excerpt}`;
};

const getArticleQualityScore = (article = {}) => {
  let score = 0;

  if (article?.imageUrl) score += 2;
  if (article?.link) score += 1;
  score += Math.min((article?.excerpt || "").length, 240) / 240;

  return score;
};

const dedupeArticles = (articles = []) => {
  const deduped = [];
  const keyToIndexMap = new Map();

  for (const article of articles) {
    const key = getArticleDedupKey(article);
    const existingIndex = keyToIndexMap.get(key);

    if (existingIndex === undefined) {
      keyToIndexMap.set(key, deduped.length);
      deduped.push(article);
      continue;
    }

    const existing = deduped[existingIndex];
    if (getArticleQualityScore(article) > getArticleQualityScore(existing)) {
      deduped[existingIndex] = article;
    }
  }

  return deduped;
};

const FALLBACK_NEWS_IMAGE = `data:image/svg+xml;utf8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 630"><defs><linearGradient id="g" x1="0" x2="1" y1="0" y2="1"><stop offset="0%" stop-color="#1e293b"/><stop offset="100%" stop-color="#0f172a"/></linearGradient></defs><rect width="1200" height="630" fill="url(#g)"/><circle cx="180" cy="120" r="220" fill="#6366f1" opacity="0.18"/><circle cx="980" cy="560" r="260" fill="#22d3ee" opacity="0.14"/><text x="600" y="320" text-anchor="middle" fill="#cbd5e1" font-family="Arial, sans-serif" font-size="54" font-weight="700">AI News</text></svg>')}`;

const News = () => {
  const backendUrl = useMemo(() => getBackendUrl(), []);
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    const fetchNews = async () => {
      try {
        setIsLoading(true);
        setError("");

        const response = await fetch(`${backendUrl}/api/news`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const payload = await response.json();
        const data = Array.isArray(payload?.data) ? payload.data : [];

        const normalized = data.map((item, index) => {
          const excerpt = item?.description || item?.content || "No summary available for this article yet.";
          const category = Array.isArray(item?.category)
            ? item.category[0]
            : item?.category || "AI";

          return {
            id: item?.article_id || item?.link || `${item?.title || "article"}-${index}`,
            title: item?.title || "Untitled article",
            excerpt,
            date: formatDate(item?.pubDate),
            readTime: estimateReadTime(excerpt),
            category,
            imageUrl: resolveImageUrl(item),
            link: item?.link,
          };
        });

        setArticles(dedupeArticles(normalized));
      } catch (err) {
        if (err.name !== "AbortError") {
          setError("Unable to load news right now. Please try again shortly.");
          setArticles([]);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchNews();

    return () => controller.abort();
  }, [backendUrl]);

  const content = useMemo(() => {
    if (isLoading) {
      return <p className="text-slate-400 text-center col-span-full">Loading latest AI news...</p>;
    }

    if (error) {
      return <p className="text-rose-300 text-center col-span-full">{error}</p>;
    }

    if (!articles.length) {
      return <p className="text-slate-400 text-center col-span-full">No articles available right now.</p>;
    }

    return articles.map((article, i) => {
      const Card = article.link ? motion.a : motion.article;

      return (
        <Card
          key={article.id}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.1, duration: 0.5 }}
          whileHover={{ y: -6 }}
          className={`block w-full h-[520px] rounded-2xl overflow-hidden group ${article.link ? "cursor-pointer" : "cursor-default"}`}
          style={{ background: "rgba(30, 41, 59, 0.4)", backdropFilter: "blur(12px)", border: "1px solid rgba(99, 102, 241, 0.1)" }}
          {...(article.link
            ? {
              href: article.link,
              target: "_blank",
              rel: "noreferrer",
              "aria-label": `Open article: ${article.title}`,
            }
            : {})}
        >
          <div className="h-1" style={{ background: "linear-gradient(90deg, #6366F1, #22D3EE, #A78BFA)" }} />

          <div className="h-48 overflow-hidden border-b border-white/5">
            <img
              src={article.imageUrl || FALLBACK_NEWS_IMAGE}
              alt={article.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = FALLBACK_NEWS_IMAGE;
              }}
            />
          </div>

          <div className="p-6 h-[calc(100%-12.25rem)] flex flex-col">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-accent/10 text-accent-light border border-accent/20">
                <Tag className="w-3 h-3" />
                {article.category}
              </span>
            </div>

            <h3
              className="text-lg font-bold text-white mb-3 group-hover:text-ai-glow transition-colors leading-snug"
              style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
            >
              {article.title}
            </h3>
            <p
              className="text-sm text-slate-400 leading-relaxed mb-5"
              style={{ display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden" }}
            >
              {article.excerpt}
            </p>

            <div className="mt-auto flex items-center justify-between pt-4 border-t border-white/5">
              <div className="flex items-center gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {article.readTime}
                </span>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-ai-glow group-hover:translate-x-1 transition-all" />
            </div>
          </div>
        </Card>
      );
    });
  }, [articles, error, isLoading]);

  return (
    <main className="bg-primary">
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 right-1/4 w-96 h-96 rounded-full opacity-15 blur-3xl" style={{ background: "radial-gradient(circle, #A78BFA, transparent)" }} />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="inline-block px-4 py-1.5 rounded-full bg-secondary/10 border border-secondary/20 text-secondary text-sm font-medium mb-6">
              Insights
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6">
              News & <span className="gradient-text">Articles</span>
            </h1>
            <p className="text-xl text-slate-400">
              Latest insights, research, and updates from the world of AI
            </p>
          </motion.div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {content}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="section-padding">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-2xl p-8 sm:p-12"
            style={{
              background: "linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(34, 211, 238, 0.08))",
              border: "1px solid rgba(99, 102, 241, 0.2)",
            }}
          >
            <h2 className="text-2xl font-bold text-white mb-3">Stay Updated with AI Insights</h2>
            <p className="text-slate-400 mb-6">Subscribe to our newsletter for the latest in AI research and trends.</p>
            <form className="flex flex-col sm:flex-row gap-3" onSubmit={(e) => e.preventDefault()}>
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-xl bg-surface/50 text-white placeholder-slate-500 border border-accent/10 focus:border-accent/30 focus:outline-none text-sm"
              />
              <button type="submit" className="neon-btn neon-btn-primary whitespace-nowrap">
                Subscribe
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      <FooterNew />
    </main>
  );
};

export default News;
